/** HTML 落地页投放标记与下载状态展示（AE §11.4.1）；投放是运营手动标记，与发布互不影响。 */
import type { LandingPageDeliveryStatus } from '@novel-admin/shared';
import type { ProjectTask } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';

export function LandingPageDeliveryChip({
  status,
  outdated,
}: {
  status: LandingPageDeliveryStatus;
  outdated: boolean;
}) {
  if (status !== 'DELIVERING') return <span className="chip">未投放</span>;
  return outdated ? (
    <span className="chip chip-warn" title="投放的是旧版本，需重新下载并上传">
      投放中 · 旧版本
    </span>
  ) : (
    <span className="chip chip-pos">投放中</span>
  );
}

export function LandingPageDownloadChip({ count }: { count: number }) {
  return count > 0 ? (
    <span className="chip chip-info">
      已下载（<span className="mono">{count}</span> 次）
    </span>
  ) : (
    <span className="chip">未下载</span>
  );
}

export function LandingPageDeliveryCell({
  task,
  blockedReason,
  pending,
  onChange,
}: {
  task: ProjectTask;
  /** 项目级阻断（书籍不可投放 / 项目归档）；只拦「投放」，不拦「停止投放」。 */
  blockedReason: string | null;
  pending: boolean;
  onChange: (status: LandingPageDeliveryStatus) => void;
}) {
  const delivering = task.delivery_status === 'DELIVERING';
  const startBlockedReason = task.artifact_path
    ? blockedReason
    : '尚未生成 HTML，生成后才能标记投放';
  return (
    <div className="vstack" style={{ gap: 6, alignItems: 'flex-start' }}>
      <LandingPageDeliveryChip status={task.delivery_status} outdated={task.delivery_outdated} />
      {delivering && task.delivery_outdated && (
        <span style={{ color: 'var(--warn)', fontSize: 'var(--text-xs)' }}>需重新下载并上传</span>
      )}
      {delivering && task.delivery_updated_at && (
        <span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
          {fmtDateTime(task.delivery_updated_at)}
        </span>
      )}
      <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
        {(!delivering || task.delivery_outdated) && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={pending || !!startBlockedReason}
            title={
              startBlockedReason ?? (delivering ? '广告平台已换成当前版本后确认' : '标记为投放中')
            }
            onClick={() => onChange('DELIVERING')}
          >
            {delivering ? '确认新版本' : '投放'}
          </button>
        )}
        {delivering && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={pending}
            onClick={() => onChange('NOT_DELIVERING')}
          >
            停止投放
          </button>
        )}
      </div>
    </div>
  );
}

export function LandingPageDownloadCell({ task }: { task: ProjectTask }) {
  return (
    <div className="vstack" style={{ gap: 4, alignItems: 'flex-start' }}>
      <LandingPageDownloadChip count={task.download_count} />
      {task.download_count > 0 && task.last_downloaded_at && (
        <span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
          {fmtDateTime(task.last_downloaded_at)}
        </span>
      )}
    </div>
  );
}
